// Quick-add combos: one-click layer stacks. Adds multiple layers at once
// with sensible defaults, so a user can go from empty to interesting in
// a single click.

import { addLayer } from '@/core/state';
import { makeDefaultLayer } from '@/core/pipeline';
import type { Layer, LayerKind } from '@/core/types';

export type ComboLayer = {
  kind: LayerKind;
  params?: Record<string, unknown>;
  opacity?: number;
};

export type Combo = {
  id: string;
  name: string;
  hint: string;
  layers: ComboLayer[];
};

export const COMBOS: Combo[] = [
  {
    id: 'noise-pack',
    name: 'Noise Pack',
    hint: 'perlin → dither → palette',
    layers: [
      { kind: 'perlin', params: { scale: 32, octaves: 4, contrast: 1.4 } },
      { kind: 'dither', params: { algo: 'bayer8', levels: 4 } },
      { kind: 'palette', params: { palette: 'sweetie-16' } },
    ],
  },
  {
    id: 'voronoi-pack',
    name: 'Voronoi Pack',
    hint: 'voronoi → palette',
    layers: [
      { kind: 'voronoi', params: { sites: 16, hue: 200, spread: 60, edges: false } },
      { kind: 'palette', params: { palette: 'kirokaze-gb' } },
    ],
  },
  {
    id: 'image-to-pixel',
    name: 'Image → Pixel',
    hint: 'image → dither → palette',
    layers: [
      { kind: 'image', params: { fit: 'contain' } },
      { kind: 'dither', params: { algo: 'floyd', levels: 4 } },
      { kind: 'palette', params: { palette: 'gb-original' } },
    ],
  },
  {
    id: 'gradient-stack',
    name: 'Gradient Stack',
    hint: 'gradient → posterize → palette',
    layers: [
      { kind: 'gradient', params: { type: 'radial', a: '#1a1c2c', b: '#ff5a1f', power: 1.4 } },
      { kind: 'posterize', params: { levels: 5 } },
      { kind: 'palette', params: { palette: 'duotone-orange' } },
    ],
  },
  {
    id: 'kaleido',
    name: 'Kaleido',
    hint: 'noise → symmetry → palette',
    layers: [
      { kind: 'perlin', params: { scale: 36, octaves: 4, contrast: 1.5 } },
      { kind: 'symmetry', params: { mode: 'kaleido' } },
      { kind: 'palette', params: { palette: 'twilight-5' } },
    ],
  },
  {
    id: 'pulse',
    name: 'Pulse (animated)',
    hint: 'cellular → palette',
    layers: [
      { kind: 'cellular', params: { density: 0.35 } },
      { kind: 'palette', params: { palette: 'pico-8' } },
    ],
  },
];

export function applyCombo(combo: Combo): void {
  for (const cl of combo.layers) {
    const tmpl = makeDefaultLayer(cl.kind, '_');
    const layer: Omit<Layer, 'id'> = {
      kind: tmpl.kind,
      params: { ...tmpl.params, ...(cl.params ?? {}) },
      blend: tmpl.blend,
      opacity: cl.opacity ?? tmpl.opacity,
      enabled: tmpl.enabled,
      seed: tmpl.seed,
    };
    addLayer(layer);
  }
}
